import Auth from '@aws-amplify/auth';
import sportsheadzApi from '../apis/sportsheadzApi';
import createDataProvider from './createDataProvider';
import { providerContextReducer } from './reducer';
import { triggerNotification } from '../helpers/notificationHelper';
import onApiRequest from './ApiRequestHandler';
import { buildURLSearchParams } from '../helpers/format';

const initialPaginatedState = {
  pageIndex: 1,
  pageSize: 50,
  totalCount: 0,
  totalPages: 0,
  totalNoFilterCount: 0,
  data: [],
  filter: null,
  loading: false,
  error: null
};

const initialState = {
  paginatedEcomOrders: { ...initialPaginatedState },
  paginatedRegAssociationCustomReports: { ...initialPaginatedState },
  paginatedRegCartMerchPackages: { ...initialPaginatedState },
  paginatedRegCartOrders: { ...initialPaginatedState },
  paginatedRegCustomers: { ...initialPaginatedState },
  paginatedRegFormSubmissions: { ...initialPaginatedState },
  paginatedRegPersons: { ...initialPaginatedState },
  paginatedRegScoutReportEvaluationSessions: { ...initialPaginatedState },
  paginatedRegScoutReportPersons: { ...initialPaginatedState },
  paginatedRegScoutReportTemplates: { ...initialPaginatedState },
  ecomOrder: { value: null, loading: false, error: null },
  regAssociations: { value: [], loading: false, error: null },
  regAssociation: { value: null, loading: false, error: null },
  regAssociationAdmins: { value: [], loading: false, error: null },
  regAssociationAdminInvite: { value: null, loading: false, error: null },
  regAssociationAnalyticsReport: { value: null, loading: false, error: null },
  regAssociationRegistrants: { value: [], loading: false, error: null },
  regAssociationCoupons: { value: [], loading: false, error: null },
  regAssociationEvaluator: { value: null, loading: false, error: null },
  regAssociationEvaluators: { value: [], loading: false, error: null },
  regAssociationGroupCoupons: { value: [], loading: false, error: null },
  regAssociationOptionsSummary: { value: null, loading: false, error: null },
  regAssociationPaymentPlan: { value: null, loading: false, error: null },
  regAssociationPaymentPlans: { value: [], loading: false, error: null },
  regAssociationResourceOptions: { value: null, loading: false, error: null },
  regAssociationSeasons: { value: [], loading: false, error: null },
  regAssociationDivisionFormExternalInvites: {
    value: [],
    loading: false,
    error: null
  },
  regAssociationDivisionFormInvites: { value: [], loading: false, error: null },
  regAssociationDivisions: { value: [], loading: false, error: null, type: '' },
  regAssociationDivisionRegistrants: { value: [], loading: false, error: null },
  regAssociationDivisionTeamOptions: {
    value: null,
    loading: false,
    error: null
  },
  regAssociationDivisionTeams: { value: [], loading: false, error: null },
  regAssociationDivisionTeamsSummary: {
    value: [],
    loading: false,
    error: null
  },
  regAssociationSavedTemplates: { value: [], loading: false, error: null },
  regAssociationTaskTemplates: { value: [], loading: false, error: null },
  regCart: { value: null, loading: false, error: null },
  regCartMerchPackageFilters: { value: null, loading: false, error: null },
  regCartMerchPackageBalance: { value: null, loading: false, error: null },
  regCustomer: { value: null, loading: false, error: null },
  regEmailUnsubscribe: { value: [], loading: false, error: null },
  regForm: { value: null, loading: false, error: null },
  regForms: { value: [], loading: false, error: null },
  regFormVersionHistory: { value: null, loading: false, error: null },
  regFormSubmission: { value: null, loading: false, error: null },
  regFormSubmissionBalance: { value: null, loading: false, error: null },
  regFormSubmissionOrderHistory: { value: null, loading: false, error: null },
  regFormSubmissions: { value: [], loading: false, error: null },
  regFormSubmissionsExport: { value: [], loading: false, error: null },
  regFormSubmissionPaymentTransactionDetails: {
    value: null,
    loading: false,
    error: null
  },
  regFormSubmissionInvoiceTransactionDetails: {
    value: null,
    loading: false,
    error: null
  },
  regPerson: { value: null, loading: false, error: null },
  regReportInputSources: { value: [], loading: false, error: null },
  regReportResult: { value: null, loading: false, error: null },
  regStaffReportResult: { value: null, loading: false, error: null },
  regReports: { value: [], loading: false, error: null },
  regResource: { value: null, loading: false, error: null },
  regSpecialRequests: { value: [], loading: false, error: null },
  regScoutReportPersons: { value: [], loading: false, error: null },
  regScoutReport: { value: null, loading: false, error: null },
  regScoutReports: { value: [], loading: false, error: null },
  regScoutReportEvaluationGenerateOptions: {
    value: null,
    loading: false,
    error: null
  },
  regScoutReportEvaluationOptions: { value: null, loading: false, error: null },
  regScoutReportEvaluationPlayers: { value: [], loading: false, error: null },
  regScoutReportEvaluationSession: { value: null, loading: false, error: null },
  regScoutReportEvaluationSessions: { value: [], loading: false, error: null },
  regWaitlistRegistrants: { value: [], loading: false, error: null },
  sesEmailSessions: { value: [], loading: false, error: null }
};

const defaultFetchListConfig = {
  resetStateWhenFetching: true,
  dataSelector: null,
  onErrorCallback: null,
  onSuccessCallback: null,
  triggerErrorNotification: true,
  filter: null,
  params: null
};

// #region Helpers
const getErrorMessage = (e) => {
  let error = 'Oops something went wrong!';
  let type = 'general';
  if (e?.response?.status === 409 && e.response.data) {
    error = e.response.data.error;
    type = e.response.data.type;
  }
  return { error, type };
};

const handleError = (
  e,
  stateName,
  dispatch,
  onErrorCallback = null,
  triggerErrorNotification = false
) => {
  const { error, type } = getErrorMessage(e);

  if (stateName && dispatch) {
    dispatch({
      type: 'error',
      stateName,
      payload: error
    });
  }

  if (triggerErrorNotification) {
    triggerNotification(error);
  }

  if (onErrorCallback) {
    onErrorCallback(error, type);
  }
};

const fetchListResource = async (
  dispatch,
  stateName,
  apiPath,
  config = defaultFetchListConfig
) => {
  const fetchConfig = {
    ...defaultFetchListConfig,
    ...config
  };
  const fetchFilter =
    config.filter || config.params
      ? { ...config.filter, ...config.params }
      : null;

  try {
    dispatch({
      type: 'fetching',
      stateName,
      payload: fetchConfig.resetStateWhenFetching
        ? { value: [], filter: fetchFilter }
        : null
    });

    const response = await sportsheadzApi.get(apiPath, {
      params: fetchConfig.params,
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`
      }
    });

    dispatch({
      type: 'success',
      stateName,
      payload: {
        filter: fetchFilter,
        value: fetchConfig.dataSelector
          ? response.data[fetchConfig.dataSelector]
          : response.data
      }
    });

    if (fetchConfig.onSuccessCallback) {
      fetchConfig.onSuccessCallback(response.data);
    }

    return response;
  } catch (e) {
    handleError(
      e,
      stateName,
      dispatch,
      fetchConfig.onErrorCallback,
      fetchConfig.triggerErrorNotification
    );
  }
  return null;
};
// #endregion

// #region RegAssociations
const fetchAdminRegAssociations = (dispatch) => async (
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociations'
    });

    const response = await sportsheadzApi.get(
      '/api/registration/admin/associations',
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regAssociations',
      payload: { value: response.data }
    });

    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regAssociations',
      payload: 'Oops something went wrong'
    });
    if (onErrorCallback) {
      onErrorCallback();
    }
  }
};

const fetchAdminRegAssociationLeagueOptions = (dispatch) => async (
  pkRegAssociation,
  { includeDeleted } = {}
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociation'
    });

    const response = await sportsheadzApi.get(
      `/api/registration/${pkRegAssociation}/league-options`,
      {
        params: { includeDeleted },
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regAssociation',
      payload: { value: response.data }
    });
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regAssociation',
      payload: 'Oops something went wrong'
    });
  }
};

const fetchRegAssociationOptionsSummary = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  onApiRequest(
    {
      url: `/api/registration/admin/${pkRegAssociation}/options-summary`,
      type: 'get',
      params: { pkRegAssociation },
      successAction: 'success',
      dispatch,
      resetStateValue: null,
      stateName: 'regAssociationOptionsSummary'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchRegAssociationResourceOptions = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  onApiRequest(
    {
      url: `/api/registration/${pkRegAssociation}/resource-options`,
      type: 'get',
      params: { pkRegAssociation },
      successAction: 'success',
      dispatch,
      resetStateValue: null,
      stateName: 'regAssociationResourceOptions'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateRegAssociation = (dispatch) => async (
  pkRegAssociation,
  {
    primaryColor,
    secondaryColor,
    supportEmail,
    treasurerAlertsEmail,
    sortOverride,
    heroImageUrl,
    logoImageUrl,
    lightModeTheme,
    fkRegFormSortType,
    fkCurrencyType,
    isSortDescending,
    sandboxEnabled,
    socials,
    sidebars
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/${pkRegAssociation}`,
      {
        heroImageUrl,
        logoImageUrl,
        primaryColor,
        secondaryColor,
        supportEmail,
        treasurerAlertsEmail,
        sortOverride,
        lightModeTheme,
        fkRegFormSortType,
        fkCurrencyType,
        isSortDescending,
        sandboxEnabled,
        socials,
        sidebars
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociations',
      payload: {
        value: response.data,
        filter: (f) => f.pkRegAssociation !== response.data.pkRegAssociation
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociations', dispatch, onErrorCallback);
  }
};

const onboardPaymentProvider = (dispatch) => async (
  pkRegAssociation,
  {
    accountId,
    accountPassword,
    regAssociationPaymentProviderType,
    flatRateFee,
    percentageRateFee,
    rebasePaymentFees
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/payment/registration/onboard/${pkRegAssociation}`,
      {
        accountId,
        accountPassword,
        regAssociationPaymentProviderType,
        flatRateFee,
        percentageRateFee,
        rebasePaymentFees
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, null, null, onErrorCallback);
  }
};

const disconnectPaymentProvider = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/payment/registration/onboard/disconnect?pkRegAssociation=${pkRegAssociation}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, null, null, onErrorCallback);
  }
};
// #endregion

// #region Reports
const fetchPaginatedCustomReports = (dispatch) => async (
  pkRegAssociation,
  { search, regFormUuid, sort, page, pageSize, appendResults, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedRegAssociationCustomReports',
      url: `/api/registration/admin/association/${pkRegAssociation}/reports/custom`,
      type: 'get',
      params: {
        search,
        regFormUuid,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchAdminRegReports = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regReports',
      payload: {
        value: [],
        filter: { pkRegAssociation }
      }
    });

    const response = await fetchListResource(
      dispatch,
      'regAssociationSavedTemplates',
      `/api/registration/admin/association/reports/${pkRegAssociation}`,
      {
        filter: { pkRegAssociation },
        onSuccessCallback,
        onErrorCallback,
        dataSelector: 'regAssociationSavedTemplates'
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regReports',
      payload: {
        value: response.data.regReportTemplates,
        filter: { pkRegAssociation }
      }
    });
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regReports',
      payload: 'Oops something went wrong'
    });
  }
};

const fetchAdminRegReportInputSources = (dispatch) => async (
  pkRegAssociation,
  pkRegReportTemplate
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regReportInputSources'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/admin/association/reports/sources/${pkRegAssociation}/${pkRegReportTemplate}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regReportInputSources',
      payload: {
        value: response.data,
        filter: (f) =>
          !response.data.some(
            (d) =>
              d.type === f.type && d.fkRegAssociation === f.fkRegAssociation
          )
      }
    });
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regReportInputSources',
      payload: 'Oops something went wrong'
    });
  }
};

const generateAdminRegAssociationReport = (dispatch) => async (
  {
    pkRegAssociation,
    pkRegReportTemplate,
    inputAnswers,
    regExternalConnections
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regReportResult'
    });
    const response = await sportsheadzApi.post(
      `/api/registration/admin/association/reports/generate/${pkRegReportTemplate}`,
      { pkRegAssociation, inputAnswers, regExternalConnections },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regReportResult',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regReportResult', dispatch, onErrorCallback);
  }
};

const generateStaffRegistrationReport = (dispatch) => async (
  {
    reportGuid,
    pkRegAssociation,
    inputAnswers,
    startDate,
    endDate,
    includeTesting
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/staff/registration/reports/generate`,
      {
        reportGuid,
        pkRegAssociation,
        inputAnswers,
        startDate,
        endDate,
        includeTesting
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regStaffReportResult',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    if (onErrorCallback) {
      onErrorCallback();
    }
  }
};

const createAdminRegAssociationSavedTemplate = (dispatch) => async (
  { name, description, fkRegForm, regAssociationSavedTemplateFormInputs },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationSavedTemplates'
    });
    const response = await sportsheadzApi.post(
      `/api/registration/admin/reports/saved-templates`,
      { name, description, fkRegForm, regAssociationSavedTemplateFormInputs },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regAssociationSavedTemplates',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regAssociationSavedTemplates', dispatch, onErrorCallback);
  }
};

const updateAdminRegAssociationSavedTemplate = (dispatch) => async (
  pkRegAssociationSavedTemplate,
  { name, description, regAssociationSavedTemplateFormInputs },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationSavedTemplates'
    });
    const response = await sportsheadzApi.put(
      `/api/registration/admin/reports/saved-templates/${pkRegAssociationSavedTemplate}`,
      { name, description, regAssociationSavedTemplateFormInputs },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationSavedTemplates',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationSavedTemplate !== pkRegAssociationSavedTemplate
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationSavedTemplates', dispatch, onErrorCallback);
  }
};

const deleteAdminRegAssociationSavedTemplate = (dispatch) => async (
  pkRegAssociationSavedTemplate,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationSavedTemplates'
    });
    await sportsheadzApi.delete(
      `/api/registration/admin/reports/saved-templates/${pkRegAssociationSavedTemplate}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'remove',
      stateName: 'regAssociationSavedTemplates',
      payload: (t) =>
        t.pkRegAssociationSavedTemplate !== pkRegAssociationSavedTemplate
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationSavedTemplates', dispatch, onErrorCallback);
  }
};

const generateAdminRegAssociationSavedTemplate = (dispatch) => async (
  pkRegAssociationSavedTemplate,
  { inputAnswers, regExternalConnections },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regReportResult'
    });
    const response = await sportsheadzApi.post(
      `/api/registration/admin/reports/saved-templates/${pkRegAssociationSavedTemplate}/generate`,
      { inputAnswers, regExternalConnections },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regReportResult',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regReportResult', dispatch, onErrorCallback);
  }
};

const generateAdminRegAssociationAnalyticsReport = (dispatch) => async (
  pkRegAssociation,
  {
    uuid,
    startDate,
    endDate,
    noDateRange,
    compareStartDate,
    compareEndDate,
    periodInterval,
    groupBy,
    compareBy,
    resourceType,
    headers,
    options,
    filters
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationAnalyticsReport',
      url: `/api/registration/admin/association/${pkRegAssociation}/analytics/generate/${uuid}`,
      type: 'post',
      successAction: 'success',
      data: {
        startDate,
        endDate,
        noDateRange,
        compareStartDate,
        compareEndDate,
        periodInterval,
        groupBy,
        compareBy,
        resourceType,
        headers,
        options,
        filters
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Coupons
const fetchAdminRegAssociationCoupons = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationCoupons',
      payload: {
        value: [],
        filter: { pkRegAssociation }
      }
    });

    const response = await fetchListResource(
      dispatch,
      'regAssociationGroupCoupons',
      `/api/registration/admin/association/coupons/${pkRegAssociation}`,
      {
        filter: { pkRegAssociation },
        onSuccessCallback,
        onErrorCallback,
        dataSelector: 'groupCoupons'
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regAssociationCoupons',
      payload: {
        value: response.data.coupons,
        filter: { pkRegAssociation }
      }
    });
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regAssociationCoupons',
      payload: 'Oops something went wrong'
    });
  }
};

const createAdminRegAssociationCoupon = (dispatch) => async (
  pkRegAssociation,
  {
    code,
    maxRedemptions,
    flatDiscount,
    percentageDiscount,
    fkRegAssociationSeason,
    fkRegAssociationDivision,
    fkRegAssociationDivisionForm
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/association/coupons/${pkRegAssociation}`,
      {
        code,
        maxRedemptions,
        flatDiscount,
        percentageDiscount,
        fkRegAssociationSeason,
        fkRegAssociationDivision,
        fkRegAssociationDivisionForm
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
    dispatch({
      type: 'append',
      stateName: 'regAssociationCoupons',
      payload: { value: response.data }
    });
  } catch (e) {
    handleError(e, 'regAssociationCoupons', dispatch, onErrorCallback);
  }
};

const updateAdminRegAssociationCoupon = (dispatch) => async (
  pkRegAssociationCoupon,
  {
    maxRedemptions,
    fkRegAssociationSeason,
    fkRegAssociationDivision,
    fkRegAssociationDivisionForm,
    reinstate
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/association/coupons/${pkRegAssociationCoupon}`,
      {
        maxRedemptions,
        fkRegAssociationSeason,
        fkRegAssociationDivision,
        fkRegAssociationDivisionForm,
        reinstate
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationCoupons',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationCoupon !== response.data.pkRegAssociationCoupon
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationCoupons', dispatch, onErrorCallback);
  }
};

const expireAdminRegAssociationCoupon = (dispatch) => async (
  pkRegAssociationCoupon,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.delete(
      `/api/registration/admin/association/coupons/${pkRegAssociationCoupon}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
    dispatch({
      type: 'replace',
      stateName: 'regAssociationCoupons',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationCoupon !== response.data.pkRegAssociationCoupon
      }
    });
  } catch (e) {
    handleError(e, 'regAssociationCoupons', dispatch, onErrorCallback);
  }
};

const createAdminRegAssociationGroupCoupon = (dispatch) => async (
  pkRegAssociation,
  {
    code,
    maxRedemptions,
    flatDiscount,
    percentageDiscount,
    divisionForms,
    minimumQualifiedAmount,
    qualifyPerItem
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/association/group-coupons/${pkRegAssociation}`,
      {
        code,
        maxRedemptions,
        flatDiscount,
        percentageDiscount,
        divisionForms,
        minimumQualifiedAmount,
        qualifyPerItem
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
    dispatch({
      type: 'append',
      stateName: 'regAssociationGroupCoupons',
      payload: { value: response.data }
    });
  } catch (e) {
    handleError(e, 'regAssociationGroupCoupons', dispatch, onErrorCallback);
  }
};

const updateAdminRegAssociationGroupCoupon = (dispatch) => async (
  pkRegAssociationGroupCoupon,
  { maxRedemptions, minimumQualifiedAmount, divisionForms, reinstate },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/association/group-coupons/${pkRegAssociationGroupCoupon}`,
      {
        maxRedemptions,
        divisionForms,
        minimumQualifiedAmount,
        reinstate
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationGroupCoupons',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationGroupCoupon !==
          response.data.pkRegAssociationGroupCoupon
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationGroupCoupons', dispatch, onErrorCallback);
  }
};

const expireAdminRegAssociationGroupCoupon = (dispatch) => async (
  pkRegAssociationCoupon,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.delete(
      `/api/registration/admin/association/group-coupons/${pkRegAssociationCoupon}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
    dispatch({
      type: 'replace',
      stateName: 'regAssociationGroupCoupons',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationGroupCoupon !==
          response.data.pkRegAssociationGroupCoupon
      }
    });
  } catch (e) {
    handleError(e, 'regAssociationGroupCoupons', dispatch, onErrorCallback);
  }
};
// #endregion

// #region Payment plans
const fetchAdminRegAssociationPaymentPlans = (dispatch) => async (
  pkRegAssociation,
  { pkRegAssociationPaymentPlans = null } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const searchParams = buildURLSearchParams({ pkRegAssociationPaymentPlans });

  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationPaymentPlans',
      url: `/api/registration/admin/association/${pkRegAssociation}/payment-plans?${searchParams.toString()}`,
      type: 'get',
      filter: { pkRegAssociation }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchAdminRegAssociationPaymentPlan = (dispatch) => async (
  pkRegAssociation,
  pkRegAssociationPaymentPlan,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationPaymentPlan',
      url: `/api/registration/admin/association/${pkRegAssociation}/payment-plans/${pkRegAssociationPaymentPlan}`,
      type: 'get',
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createAdminRegAssociationPaymentPlan = (dispatch) => async (
  pkRegAssociation,
  {
    name,
    description,
    active,
    fkRegAssociationPaymentPlanType,
    regAssociationPaymentPlanInstallments,
    pkRegAssociationDivisionForms
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationPaymentPlan',
      url: `/api/registration/admin/association/${pkRegAssociation}/payment-plans`,
      type: 'post',
      data: {
        name,
        description,
        active,
        fkRegAssociationPaymentPlanType,
        regAssociationPaymentPlanInstallments,
        pkRegAssociationDivisionForms
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateAdminRegAssociationPaymentPlans = (dispatch) => async (
  pkRegAssociation,
  { selectFirstItem, regAssociationPaymentPlans },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationPaymentPlans',
      url: `/api/registration/admin/association/${pkRegAssociation}/payment-plans`,
      type: 'put',
      data: regAssociationPaymentPlans,
      successAction: 'replace',
      removeFilter: (item) =>
        regAssociationPaymentPlans.every(
          (plan) =>
            plan.pkRegAssociationPaymentPlan !==
            item.pkRegAssociationPaymentPlan
        )
    },
    (data) => {
      if (selectFirstItem) {
        dispatch({
          type: 'success',
          stateName: 'regAssociationPaymentPlan',
          payload: { value: data[0] }
        });
      }

      if (onSuccessCallback) {
        onSuccessCallback(data);
      }
    },
    onErrorCallback
  );
};

const deleteAdminRegAssociationPaymentPlans = (dispatch) => async (
  pkRegAssociation,
  pkRegAssociationPaymentPlans,
  onSuccessCallback,
  onErrorCallback
) => {
  const searchParams = buildURLSearchParams({ pkRegAssociationPaymentPlans });

  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationPaymentPlans',
      url: `/api/registration/admin/association/${pkRegAssociation}/payment-plans?${searchParams.toString()}`,
      type: 'delete',
      filter: {},
      successAction: 'remove',
      removeFilter: (f) =>
        !pkRegAssociationPaymentPlans.includes(f.pkRegAssociationPaymentPlan)
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Association League
const fetchRegAssociationDivisionTeams = (dispatch) => async (
  {
    pkRegAssociation,
    pkRegAssociationSeason,
    pkRegAssociationDivision,
    summaryOnly
  },
  onSuccessCallback,
  onErrorCallback
) => {
  dispatch({
    type: 'fetching',
    stateName: summaryOnly
      ? 'regAssociationDivisionTeamsSummary'
      : 'regAssociationDivisionTeams',
    payload: {}
  });

  const paginateFetch = (page = 1) => {
    onApiRequest(
      {
        url: `/api/registration/admin/league/teams`,
        type: 'get',
        params: {
          pkRegAssociation,
          pkRegAssociationSeason,
          pkRegAssociationDivision,
          summaryOnly,
          page
        }
      },
      (d) => {
        dispatch({
          type: page && page > 1 ? 'append' : 'success',
          stateName: summaryOnly
            ? 'regAssociationDivisionTeamsSummary'
            : 'regAssociationDivisionTeams',
          payload: { value: [...d.data], loading: d.page !== d.totalPages }
        });
        if (d.page < d.totalPages) {
          paginateFetch(d.page + 1);
        }
        else if (onSuccessCallback) {
          onSuccessCallback(d);
        }
      },
      onErrorCallback
    );
  };
  paginateFetch();
};

const fetchRegAssociationDivisionTeamOptions = (dispatch) => async (
  pkRegAssociation,
  { pkRegAssociationSeasons, pkRegAssociationDivisions },
  onSuccessCallback,
  onErrorCallback
) => {
  const buildQueryParams = (key, values = []) =>
    values.map((val, index) => `${key}[${index}]=${val}`).join('&');

  const seasonParam = pkRegAssociationSeasons
    ? buildQueryParams('pkRegAssociationSeasons', pkRegAssociationSeasons)
    : null;
  const divisionParam = pkRegAssociationDivisions
    ? buildQueryParams('pkRegAssociationDivisions', pkRegAssociationDivisions)
    : null;

  const queryParts = [seasonParam, divisionParam].filter(Boolean);
  const queryString = queryParts.length ? `?${queryParts.join('&')}` : '';

  onApiRequest(
    {
      url: `/api/registration/admin/${pkRegAssociation}/teams/options${queryString}`,
      type: 'get',
      params: { pkRegAssociation },
      successAction: 'success',
      dispatch,
      resetStateValue: null,
      stateName: 'regAssociationDivisionTeamOptions'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const syncRegAssociationSeasonTeams = (dispatch) => async (
  pkRegAssociation,
  {
    pkRegAssociationSeasons,
    pkRegAssociationDivisions,
    pkRegAssociationDivisionTeams,
    removeExternalPlayers,
    skipNotification
  },
  onSuccessCallback,
  onErrorCallback
) => {
  onApiRequest(
    {
      url: `/api/registration/admin/${pkRegAssociation}/teams/sync`,
      type: 'post',
      data: {
        pkRegAssociationSeasons,
        pkRegAssociationDivisions,
        pkRegAssociationDivisionTeams,
        removeExternalPlayers,
        skipNotification
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const rebaseRegAssociationDivisionTeamConnection = (dispatch) => async (
  pkRegAssociationDivisionTeam,
  { pkTeam },
  onSuccessCallback,
  onErrorCallback
) => {
  onApiRequest(
    {
      dispatch,
      actionType: 'replace',
      stateName: 'regAssociationDivisionTeams',
      removeFilter: (f) =>
        f.pkRegAssociationDivisionTeam !== pkRegAssociationDivisionTeam,
      url: `/api/registration/admin/teams/${pkRegAssociationDivisionTeam}/rebase-connection?pkTeam=${pkTeam}`,
      type: 'put'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const importRegAssociationDivisionTeams = (dispatch) => async (
  pkRegAssociation,
  { teams },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/league/teams/import/${pkRegAssociation}`,
      { teams },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regAssociationDivisionTeams',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisionTeams', dispatch, onErrorCallback);
  }
};

const createAdminRegAssociationDivisionTeam = (dispatch) => async (
  { name, fkRegAssociationDivision, regAssociationDivisionTeamPersons },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/league/teams`,
      {
        name,
        fkRegAssociationDivision,
        regAssociationDivisionTeamPersons
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regAssociationDivisionTeams',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisionTeams', dispatch, onErrorCallback);
  }
};

const updateAdminRegAssociationDivisionTeam = (dispatch) => async (
  pkRegAssociationDivisionTeam,
  { name, fkRegAssociationDivision, regAssociationDivisionTeamPersons },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/league/teams/${pkRegAssociationDivisionTeam}`,
      {
        name,
        fkRegAssociationDivision,
        regAssociationDivisionTeamPersons
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );

    dispatch({
      type: 'replace',
      stateName: 'regAssociationDivisionTeams',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationDivisionTeam !==
          response.data.pkRegAssociationDivisionTeam
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisionTeams', dispatch, onErrorCallback);
  }
};

const deleteAdminRegAssociationDivisionTeam = (dispatch) => async (
  pkRegAssociationDivisionTeam,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(
      `/api/registration/admin/league/teams/${pkRegAssociationDivisionTeam}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'remove',
      stateName: 'regAssociationDivisionTeams',
      payload: (t) =>
        t.pkRegAssociationDivisionTeam !== pkRegAssociationDivisionTeam
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisionTeams', dispatch, onErrorCallback);
  }
};

const generateAdminRegAssociationDivisionTeams = (dispatch) => async (
  {
    pkRegAssociationDivision,
    pkRegScoutReportEvaluationSessions,
    teams,
    attributes
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.post(
      `/api/registration/admin/league/teams/generate`,
      {
        pkRegAssociationDivision,
        pkRegScoutReportEvaluationSessions,
        teams,
        attributes
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisionTeams', dispatch, onErrorCallback);
  }
};

const fetchAdminRegAssociationDivisionRegistrants = (dispatch) => async (
  pkRegAssociation,
  { pkRegAssociationDivision, pkRegAssociationSeason },
  onSuccessCallback,
  onErrorCallback
) => {
  dispatch({
    type: 'fetching',
    stateName: 'regAssociationDivisionRegistrants',
    payload: {}
  });

  const paginateFetch = (page = 1) => {
    onApiRequest(
      {
        url: `/api/registration/admin/league/registrants/${pkRegAssociation}`,
        type: 'get',
        params: {
          pkRegAssociationDivision,
          pkRegAssociationSeason,
          page
        }
      },
      (d) => {
        dispatch({
          type: page && page > 1 ? 'append' : 'success',
          stateName: 'regAssociationDivisionRegistrants',
          payload: { value: [...d.data], loading: d.page !== d.totalPages }
        });
        if (d.page < d.totalPages) {
          paginateFetch(d.page + 1);
        }
        else if (onSuccessCallback) {
          onSuccessCallback(d);
        }
      },
      onErrorCallback
    );
  };
  paginateFetch();
};

const fetchAdminRegAssociationDivisions = (dispatch) => async (
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationDivisions'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/form/divisions`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regAssociationDivisions',
      payload: { value: response.data, type: 'form-divisions' }
    });

    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisions', dispatch, onErrorCallback);
  }
};

const fetchUpdatedAdminRegAssociationDivision = (dispatch) => async (
  pkRegAssociationDivision,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationDivisions'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/admin/league/divisions/${pkRegAssociationDivision}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationDivisions',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationDivision !== response.data.pkRegAssociationDivision
      }
    });

    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(
      e,
      'regAssociationDivisionRegistrants',
      dispatch,
      onErrorCallback
    );
  }
};

const fetchAdminRegAssociationSeasons = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationDivisions',
      payload: {
        value: [],
        filter: { pkRegAssociation }
      }
    });
    const response = await fetchListResource(
      dispatch,
      'regAssociationSeasons',
      `/api/registration/admin/league/seasons/${pkRegAssociation}`,
      { onSuccessCallback, onErrorCallback, filter: { pkRegAssociation } }
    );

    dispatch({
      type: 'success',
      stateName: 'regAssociationDivisions',
      payload: {
        filter: { pkRegAssociation },
        value: response.data.reduce(
          (result, current) => result.concat(current.regAssociationDivisions),
          []
        ),
        type: 'league'
      }
    });
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regAssociationDivisions',
      payload: 'Oops something went wrong'
    });
  }
};

const createAdminRegAssociationSeason = (dispatch) => async (
  { name, endDate, fkRegAssociation, pkRegAssociationSeasonDuplicate },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/league/seasons`,
      {
        name,
        endDate,
        fkRegAssociation,
        pkRegAssociationSeasonDuplicate
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regAssociationSeasons',
      payload: { value: response.data }
    });

    if (response.data.regAssociationDivisions.length > 0) {
      dispatch({
        type: 'append',
        stateName: 'regAssociationDivisions',
        payload: { value: response.data.regAssociationDivisions }
      });
    }

    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationSeasons', dispatch, onErrorCallback);
  }
};

const updateAdminRegAssociationSeason = (dispatch) => async (
  pkRegAssociationSeason,
  { name, endDate, fkRegAssociation },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/league/seasons/${pkRegAssociationSeason}`,
      {
        name,
        endDate,
        fkRegAssociation
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationSeasons',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationSeason !== response.data.pkRegAssociationSeason
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationSeasons', dispatch, onErrorCallback);
  }
};

const deleteAdminRegAssociationSeason = (dispatch) => async (
  pkRegAssociationSeason,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(
      `/api/registration/admin/league/seasons/${pkRegAssociationSeason}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'remove',
      stateName: 'regAssociationSeasons',
      payload: (f) => f.pkRegAssociationSeason !== pkRegAssociationSeason
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationSeasons', dispatch, onErrorCallback);
  }
};

const createAdminRegAssociationDivision = (dispatch) => async (
  {
    name,
    minDob,
    maxDob,
    registrantLimit,
    waitlistExpireDays,
    waitlistEnabled,
    fkRegAssociationSeason,
    fkRegGenderType,
    roles,
    rolesRequired,
    orderEmails,
    notifyWaitlist,
    notifyWaitlistAmount,
    expireWaitlistReservations,
    offlinePaymentsEnabled,
    isInviteOnly,
    isAllAges
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/league/divisions`,
      {
        name,
        minDob,
        maxDob,
        registrantLimit,
        waitlistExpireDays,
        waitlistEnabled,
        fkRegAssociationSeason,
        fkRegGenderType,
        roles,
        rolesRequired,
        orderEmails,
        notifyWaitlist,
        notifyWaitlistAmount,
        expireWaitlistReservations,
        offlinePaymentsEnabled,
        isInviteOnly,
        isAllAges
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regAssociationDivisions',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisions', dispatch, onErrorCallback);
  }
};

const updateAdminRegAssociationDivision = (dispatch) => async (
  pkRegAssociationDivision,
  {
    name,
    minDob,
    maxDob,
    registrantLimit,
    waitlistExpireDays,
    waitlistEnabled,
    fkRegAssociationSeason,
    fkRegGenderType,
    roles,
    rolesRequired,
    orderEmails,
    notifyWaitlist,
    notifyWaitlistAmount,
    expireWaitlistReservations,
    offlinePaymentsEnabled,
    isInviteOnly,
    isAllAges
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/league/divisions/${pkRegAssociationDivision}`,
      {
        name,
        minDob,
        maxDob,
        registrantLimit,
        waitlistExpireDays,
        waitlistEnabled,
        roles,
        rolesRequired,
        orderEmails,
        fkRegAssociationSeason,
        fkRegGenderType,
        notifyWaitlist,
        notifyWaitlistAmount,
        expireWaitlistReservations,
        offlinePaymentsEnabled,
        isInviteOnly,
        isAllAges
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationDivisions',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationDivision !== response.data.pkRegAssociationDivision
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisions', dispatch, onErrorCallback);
  }
};

const deleteAdminRegAssociationDivision = (dispatch) => async (
  pkRegAssociationDivision,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(
      `/api/registration/admin/league/divisions/${pkRegAssociationDivision}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'remove',
      stateName: 'regAssociationDivisions',
      payload: (f) => f.pkRegAssociationDivision !== pkRegAssociationDivision
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationDivisions', dispatch, onErrorCallback);
  }
};

const fetchAdminRegSpecialRequests = (dispatch) => async (
  {
    pkRegAssociation,
    pkRegAssociationSeason,
    pkRegAssociationDivision,
    pkRegAssociationDivisionTeam
  },
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(
    dispatch,
    'regSpecialRequests',
    `/api/registration/admin/league/divisions/special-request`,
    {
      params: {
        pkRegAssociation,
        pkRegAssociationSeason,
        pkRegAssociationDivision,
        pkRegAssociationDivisionTeam
      },
      onSuccessCallback,
      onErrorCallback
    }
  );
};

const updateAdminRegSpecialRequests = (dispatch) => async (
  pkRegAssociationDivision,
  { regSpecialRequests },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/league/divisions/${pkRegAssociationDivision}/special-request`,
      regSpecialRequests,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regSpecialRequests',
      payload: {
        filter: (r) =>
          r.regFormSubmission1.regAssociationDivisionOverride
            ? r.regFormSubmission1.regAssociationDivisionOverride.pkRegAssociationDivision.toString() !==
              pkRegAssociationDivision.toString()
            : r.regFormSubmission1.regAssociationDivisionForm.regAssociationDivision.pkRegAssociationDivision.toString() !==
              pkRegAssociationDivision.toString(),
        value: response.data
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regSpecialRequests', dispatch, onErrorCallback);
  }
};

const fetchAdminRegAssociationRegistrants = (dispatch) => async (
  pkRegAssociation,
  { season, division, divisionTeam },
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(
    dispatch,
    'regAssociationRegistrants',
    `/api/registration/admin/registrants/${pkRegAssociation}`,
    {
      filter: { pkRegAssociation },
      params: { season, division, divisionTeam },
      onSuccessCallback,
      onErrorCallback
    }
  );
};
// #endregion

// #region RegForms
const fetchAdminRegForm = (dispatch) => async (uuid) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regForm'
    });

    const response = await sportsheadzApi.get(
      `/api/registration/form/regForm/${uuid}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regForm',
      payload: { value: response.data }
    });
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regForm',
      payload: 'Oops something went wrong'
    });
  }
};

const fetchAdminRegFormVersionHistory = (dispatch) => async (uuid) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regFormVersionHistory'
    });

    const response = await sportsheadzApi.get(
      `/api/registration/form/${uuid}/version-history`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regFormVersionHistory',
      payload: { value: response.data }
    });
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regFormVersionHistory',
      payload: 'Oops something went wrong'
    });
  }
};

const fetchAdminRegForms = (dispatch) => async (
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(dispatch, 'regForms', `/api/registration/form`, {
    onSuccessCallback,
    onErrorCallback
  });
};

const fetchPaginatedRegFormSubmissions = (dispatch) => async (
  pkRegAssociation,
  {
    season,
    division,
    form,
    status,
    hockeyCanadaIdStatus,
    search,
    page,
    pageSize,
    sortBy,
    orderBy,
    appendResults,
    skipLoading
  } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/registration/admin/form-submissions`,
      type: 'get',
      params: {
        pkRegAssociation,
        season,
        division,
        form,
        status,
        hockeyCanadaIdStatus,
        search,
        page,
        pageSize,
        sortBy,
        orderBy
      },
      transformPayload: (data) => ({ ...data }),
      successAction: appendResults ? 'paginated-append' : 'success',
      dispatch,
      stateName: 'paginatedRegFormSubmissions',
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchRegFormSubmissionExportData = (dispatch) => async (
  {
    fkRegAssociation,
    pkRegFormSubmissions,
    seaons,
    division,
    form,
    status,
    hockeyCanadaIdStatus,
    search,

    skipStateUpdate = false
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkRegFormSubmissionsParam = pkRegFormSubmissions?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkRegFormSubmissions[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      url: `/api/registration/admin/form-submissions/export-data?fkRegAssociation=${fkRegAssociation}${
        pkRegFormSubmissionsParam ? `&${pkRegFormSubmissionsParam}` : ''
      }`,
      params: {
        seaons,
        division,
        form,
        status,
        hockeyCanadaIdStatus,
        search
      },
      type: 'get',
      ...(skipStateUpdate
        ? {}
        : {
            dispatch,
            stateName: 'regFormSubmissionsExport',
            filter: {
              fkRegAssociation,
              pkRegFormSubmissions,
              seaons,
              division,
              form,
              status,
              hockeyCanadaIdStatus,
              search
            },
            resetStateValue: []
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateRegFormSubmissionsBulk = (dispatch) => async (
  pkRegAssociation,
  { regFormSubmissions },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regFormSubmissionsExport',
      url: `/api/registration/admin/registrations/${pkRegAssociation}/bulk-update`,
      type: 'put',
      data: regFormSubmissions,
      successAction: 'replace',
      removeFilter: (item, data) =>
        !data.some((s) => s.pkRegFormSubmission === item.pkRegFormSubmission)
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateRegFormSubmission = (dispatch) => async (
  pkRegAssociation,
  {
    pkRegFormSubmission,
    fkRegAssociationDivision,
    regFormSubmissionExternalIds,
    regFormSubmissionPersonContacts
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regFormSubmissionBalance',
      url: `/api/registration/admin/registrations/${pkRegAssociation}/update`,
      type: 'put',
      data: {
        pkRegFormSubmission,
        fkRegAssociationDivision,
        regFormSubmissionExternalIds,
        regFormSubmissionPersonContacts
      },
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchRegFormSubmissionRegistration = (dispatch) => async (
  pkRegFormSubmission,
  {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regFormSubmission',
      url: `/api/registration/admin/form-submissions/${pkRegFormSubmission}`,
      type: 'get',
      successAction: 'success'
    },
    (data) => {
      dispatch({
        type: 'success',
        stateName: 'regFormSubmissionBalance',
        payload: { value: data.regFormSubmission }
      });

      dispatch({
        type: 'success',
        stateName: 'regResource',
        payload: { value: data.regResource }
      });

      if (onSuccessCallback) {
        onSuccessCallback(data);
      }
    },
    onErrorCallback
  );
};

const fetchAdminFinancialRegistrations = (dispatch) => async (
  pkRegAssociation,
  { coupon, groupCoupon, season },
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(
    dispatch,
    'regFormSubmissions',
    `/api/registration/admin/registrations`,
    {
      params: { pkRegAssociation, coupon, groupCoupon, season },
      onSuccessCallback,
      onErrorCallback
    }
  );
};

const fetchUpdatedAdminFinancialRegistration = (dispatch) => async (
  pkRegFormSubmission,
  onSuccessCallback,
  hideLoading = false
) => {
  try {
    if (!hideLoading) {
      dispatch({
        type: 'fetching',
        stateName: 'regFormSubmissions'
      });
      dispatch({
        type: 'fetching',
        stateName: 'paginatedRegFormSubmissions'
      });
    }

    const response = await sportsheadzApi.get(
      `/api/registration/admin/registrations/${pkRegFormSubmission}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regFormSubmissions',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegFormSubmission !== response.data.pkRegFormSubmission
      }
    });
    dispatch({
      type: 'paginated-replace-index',
      stateName: 'paginatedRegFormSubmissions',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegFormSubmission === response.data.pkRegFormSubmission
      }
    });

    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch {
    dispatch({
      type: 'error',
      stateName: 'regFormSubmissions',
      payload: 'Oops something went wrong'
    });
    dispatch({
      type: 'error',
      stateName: 'paginatedRegFormSubmissions',
      payload: 'Oops something went wrong'
    });
  }
};

const respondToFinancialRegistration = (dispatch) => async (
  pkRegFormSubmission,
  { status, skipEmailNotice, reinstate },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/registrations/${pkRegFormSubmission}/respond`,
      { status, skipEmailNotice, reinstate },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regFormSubmissions',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegFormSubmission !== response.data.pkRegFormSubmission
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regFormSubmissions', dispatch, onErrorCallback);
  }
};

const changeRegistrationDivision = (dispatch) => async (
  pkRegFormSubmission,
  pkRegAssociationDivision,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/registrations/${pkRegFormSubmission}/division?pkRegAssociationDivision=${pkRegAssociationDivision}`,
      pkRegAssociationDivision,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regFormSubmissions',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegFormSubmission !== response.data.pkRegFormSubmission
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regFormSubmissions', dispatch, onErrorCallback);
  }
};

const changeRegistrationOverrideEmail = (dispatch) => async (
  pkRegFormSubmission,
  { overrideEmail },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/registrations/${pkRegFormSubmission}/change-email`,
      null,
      {
        params: { overrideEmail },
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regFormSubmissions',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegFormSubmission !== response.data.pkRegFormSubmission
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regFormSubmissions', dispatch, onErrorCallback);
  }
};

const refundFinancialRegistration = (dispatch) => async (
  pkRegFormSubmission,
  {
    skipWaitlist,
    unlockRefundAmount,
    partialRefundAmount,
    deactivateRegPerson,
    refundWithoutTransferReverse
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/payment/registration/cart/${pkRegFormSubmission}/refund`,
      {
        skipWaitlist,
        unlockRefundAmount,
        partialRefundAmount,
        deactivateRegPerson,
        refundWithoutTransferReverse
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regFormSubmissions',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegFormSubmission !== response.data.pkRegFormSubmission
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regFormSubmissions', dispatch, onErrorCallback);
  }
};

const fetchAdminRegFormSubmissionOrderHistory = (dispatch) => async (
  pkRegFormSubmission,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regFormSubmissionOrderHistory'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/admin/registrations/${pkRegFormSubmission}/order-history`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regFormSubmissionOrderHistory',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regFormSubmissionOrderHistory', dispatch, onErrorCallback);
  }
};

const resendRegCartOrderReceipts = (dispatch) => async (
  resendOrderReceiptsRequest,
  onSuccessCallback,
  onErrorCallback
) => {
  onApiRequest(
    {
      url: `/api/registration/orders/resend-receipt`,
      type: 'post',
      data: resendOrderReceiptsRequest
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchAdminRegFormSubmissionPayments = (dispatch) => async (
  pkRegFormSubmission,
  onSuccessCallback,
  onErrorCallback
) => {
  onApiRequest(
    {
      url: `/api/registration/admin/registrations/${pkRegFormSubmission}/balance`,
      type: 'get',
      dispatch,
      stateName: 'regFormSubmissionBalance',
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchAdminRegFormSubmissionPaymentTransactionDetails = (
  dispatch
) => async (pkRegFormSubmission, onSuccessCallback, onErrorCallback) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regFormSubmissionPaymentTransactionDetails'
    });

    const response = await sportsheadzApi.get(
      `/api/registration/admin/registrations/${pkRegFormSubmission}/payment/transaction-details`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
    dispatch({
      type: 'success',
      stateName: 'regFormSubmissionPaymentTransactionDetails',
      payload: { value: response.data }
    });
  } catch (e) {
    handleError(
      e,
      'regFormSubmissionPaymentTransactionDetails',
      dispatch,
      onErrorCallback
    );
  }
};

const fetchAdminRegFormSubmissionInvoiceTransactionDetails = (
  dispatch
) => async (pkRegFormSubmissionInvoice, onSuccess, onErrorCallback) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regFormSubmissionInvoiceTransactionDetails'
    });

    const response = await sportsheadzApi.get(
      `/api/payment/registration/invoice/${pkRegFormSubmissionInvoice}/transaction-details`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    if (onSuccess) {
      onSuccess();
    }
    dispatch({
      type: 'success',
      stateName: 'regFormSubmissionInvoiceTransactionDetails',
      payload: { value: response.data }
    });
  } catch (e) {
    handleError(
      e,
      'regFormSubmissionInvoiceTransactionDetails',
      dispatch,
      onErrorCallback
    );
  }
};

const createAdminRegFormSubmissionPayment = (dispatch) => async (
  pkRegFormSubmission,
  {
    amount,
    fkRegFormSubmissionPaymentType,
    note,
    skipEmailNotice,
    fkRegFormSubmissionInvoice
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/registrations/${pkRegFormSubmission}/payment`,
      {
        amount,
        fkRegFormSubmissionPaymentType,
        note,
        skipEmailNotice,
        fkRegFormSubmissionInvoice
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regFormSubmissionBalance',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regFormSubmissionBalance', dispatch, onErrorCallback);
  }
};

const createAdminRegForm = (dispatch) => async (
  {
    name,
    additionalInfoImage,
    pkRegFormCopy,
    startDate,
    endDate,
    description,
    fkRegAssociation,
    financialAssistanceType,
    baseCost,
    taxRate,
    enabled,
    sections,
    divisions,
    regFormExternalConnections
  },
  successCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regForm'
    });

    const response = await sportsheadzApi.post(
      `/api/registration/form`,
      {
        name,
        additionalInfoImage,
        pkRegFormCopy,
        startDate,
        endDate,
        description,
        fkRegAssociation,
        financialAssistanceType,
        baseCost,
        taxRate,
        enabled,
        sections,
        divisions,
        regFormExternalConnections
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regForm',
      payload: { value: response.data }
    });
    if (successCallback) {
      successCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regForm', dispatch, onErrorCallback);
  }
};

const updateAdminRegForm = (dispatch) => async (
  uuid,
  {
    name,
    additionalInfoImage,
    startDate,
    endDate,
    description,
    fkRegAssociation,
    financialAssistanceType,
    baseCost,
    taxRate,
    enabled,
    sections,
    divisions,
    regFormExternalConnections
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/form/${uuid}`,
      {
        name,
        additionalInfoImage,
        startDate,
        endDate,
        description,
        fkRegAssociation,
        financialAssistanceType,
        baseCost,
        taxRate,
        enabled,
        sections,
        divisions,
        regFormExternalConnections
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regForm',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regForm', dispatch, onErrorCallback);
  }
};

const deleteAdminRegForm = (dispatch) => async (
  uuid,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(`/api/registration/form/${uuid}`, {
      headers: {
        Authorization: `Bearer ${(await Auth.currentSession())
          .getIdToken()
          .getJwtToken()}`
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
    dispatch({
      type: 'remove',
      stateName: 'regForms',
      payload: (t) => t.uuid !== uuid
    });
  } catch (e) {
    handleError(e, 'regForm', dispatch, onErrorCallback);
  }
};
// #endregion

// #region Waitlist
const fetchAdminWaitlistRegistrants = (dispatch) => async (
  { pkRegAssociation, pkRegAssociationSeason, pkRegAssociationDivision },
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(
    dispatch,
    'regWaitlistRegistrants',
    `/api/registration/waitlist`,
    {
      params: {
        pkRegAssociation,
        pkRegAssociationSeason,
        pkRegAssociationDivision
      },
      onSuccessCallback,
      onErrorCallback
    }
  );
};

const removeAdminWaitlistPerson = (dispatch) => async (
  pkRegWaitlistPerson,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(
      `/api/registration/waitlist/${pkRegWaitlistPerson}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'remove',
      stateName: 'regWaitlistRegistrants',
      payload: (f) => f.pkRegWaitlistPerson !== pkRegWaitlistPerson
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regWaitlistRegistrants', dispatch, onErrorCallback);
  }
};

const sendAdminWaitlistReservation = (dispatch) => async (
  pkRegWaitlistPerson,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/waitlist/${pkRegWaitlistPerson}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regWaitlistRegistrants',
      payload: {
        value: response.data,
        filter: (f) => f.pkRegWaitlistPerson !== pkRegWaitlistPerson
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regWaitlistRegistrants', dispatch, onErrorCallback);
  }
};

const sendAdminWaitlistEmail = (dispatch) => async (
  pkRegAssociationDivision,
  { pkRegWaitlistPersons, subject, body, sendCopy },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.post(
      `/api/registration/waitlist/${pkRegAssociationDivision}/email`,
      { pkRegWaitlistPersons, subject, body, sendCopy },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regWaitlistRegistrants', dispatch, onErrorCallback);
  }
};
// #endregion

const sendAdminRegistrationEmail = (dispatch) => async (
  pkRegAssociation,
  { pkSesEmailSession, pkRegFormSubmissions, emails, subject, body, sendCopy },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/registrants/${pkRegAssociation}/send-email`,
      {
        pkSesEmailSession,
        pkRegFormSubmissions,
        emails,
        subject,
        body,
        sendCopy
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, null, null, onErrorCallback);
  }
};

// #region Invoices
const sendAdminRegistrationInvoice = (dispatch) => async (
  pkRegFormSubmission,
  {
    invoiceAmount,
    description,
    isOfflineInvoice,
    isAdditionalCharge,
    customerPaysServiceFees
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/payment/registration/invoice/${pkRegFormSubmission}`,
      {
        invoiceAmount,
        description,
        isOfflineInvoice,
        isAdditionalCharge,
        customerPaysServiceFees
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regFormSubmissions',
      payload: {
        value: response.data,
        filter: (f) => f.pkRegFormSubmission !== pkRegFormSubmission
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regFormSubmissions', dispatch, onErrorCallback);
  }
};

const updateAdminRegistrationInvoiceStatus = (dispatch) => async (
  pkRegFormSubmissionInvoice,
  { status, fkRegFormSubmissionPayments, fkRegFormSubmissionPaymentType },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/payment/registration/invoice/${pkRegFormSubmissionInvoice}`,
      { status, fkRegFormSubmissionPayments, fkRegFormSubmissionPaymentType },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regFormSubmissionBalance',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regFormSubmissionBalance', dispatch, onErrorCallback);
  }
};

const resendAdminRegistrationInvoice = (dispatch) => async (
  pkRegFormSubmissionInvoice,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.post(
      `/api/payment/registration/invoice/${pkRegFormSubmissionInvoice}/resend`,
      null,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regFormSubmissionBalance', dispatch, onErrorCallback);
  }
};

const rebaseAdminRegistrationInvoice = (dispatch) => async (
  pkRegFormSubmissionInvoice,
  { isAdditionalCharge },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.post(
      `/api/payment/registration/invoice/${pkRegFormSubmissionInvoice}/rebase?isAdditionalCharge=${isAdditionalCharge}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, null, null, onErrorCallback);
  }
};

const creditAdminRegistrationInvoice = (dispatch) => async (
  pkRegFormSubmissionInvoice,
  { creditAmount, creditReason, isOfflineRefund, unlockRefundAmount },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/payment/registration/invoice/${pkRegFormSubmissionInvoice}/credit`,
      { creditAmount, creditReason, isOfflineRefund, unlockRefundAmount },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regFormSubmissionBalance',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regFormSubmissionBalance', dispatch, onErrorCallback);
  }
};
// #endregion

// #region Division Invites
const fetchAdminRegAssociationDivisionFormInvites = (dispatch) => async (
  {
    pkRegAssociation,
    pkRegAssociationSeason,
    pkRegAssociationDivision,
    pkRegAssociationDivisionForm
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regAssociationDivisionFormInvites',
      payload: {
        value: [],
        filter: {
          pkRegAssociation,
          pkRegAssociationSeason,
          pkRegAssociationDivision,
          pkRegAssociationDivisionForm
        }
      }
    });

    const response = await fetchListResource(
      dispatch,
      'regAssociationDivisionFormExternalInvites',
      `/api/registration/admin/league/divisions/invite`,
      {
        params: {
          pkRegAssociation,
          pkRegAssociationSeason,
          pkRegAssociationDivision,
          pkRegAssociationDivisionForm
        },
        dataSelector: 'externalInvites',
        onSuccessCallback,
        onErrorCallback
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regAssociationDivisionFormInvites',
      payload: {
        value: response.data.invites,
        filter: {
          pkRegAssociation,
          pkRegAssociationSeason,
          pkRegAssociationDivision,
          pkRegAssociationDivisionForm
        }
      }
    });
  } catch (e) {
    dispatch({
      type: 'error',
      stateName: 'regAssociationDivisionFormInvites',
      payload: 'Oops something went wrong'
    });
  }
};

const expireAdminRegAssociationDivisionFormInvite = (dispatch) => async (
  pkRegAssociationDivisionFormInvite,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.delete(
      `/api/registration/admin/league/divisions/invite/${pkRegAssociationDivisionFormInvite}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationDivisionFormInvites',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationDivisionFormInvite !==
          pkRegAssociationDivisionFormInvite
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(
      e,
      'regAssociationDivisionFormInvites',
      dispatch,
      onErrorCallback
    );
  }
};

const sendAdminRegAssociationDivisionFormInvites = (dispatch) => async (
  pkRegAssociationDivision,
  {
    fkRegFormSubmissions,
    externalUsers,
    fkRegAssociationDivisionForm,
    expiresAt
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.post(
      `/api/registration/admin/league/divisions/${pkRegAssociationDivision}/invite`,
      {
        fkRegFormSubmissions,
        externalUsers,
        fkRegAssociationDivisionForm,
        expiresAt
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, null, null, onErrorCallback);
  }
};

const expireAdminRegAssociationDivisionFormExternalInvite = (
  dispatch
) => async (
  pkRegAssociationDivisionFormExternalInvite,
  { inviteCode },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.delete(
      `/api/registration/admin/league/divisions/invite/external/${pkRegAssociationDivisionFormExternalInvite}?inviteCode=${inviteCode}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationDivisionFormExternalInvites',
      payload: {
        value: response.data,
        filter: (f) =>
          f.pkRegAssociationDivisionFormExternalInvite !==
          pkRegAssociationDivisionFormExternalInvite
      }
    });
    dispatch({
      type: 'success',
      stateName: 'regAssociationDivisionFormExternalInvite',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(
      e,
      'regAssociationDivisionFormInvites',
      dispatch,
      onErrorCallback
    );
  }
};
// #endregion

// #region Evaluations

const fetchRegScoutReportEvaluationGenerateTeamOptions = (dispatch) => async (
  pkRegAssociation,
  { fkRegAssociationDivision } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regScoutReportEvaluationGenerateOptions',
      url: `/api/registration/admin/scout-reports/evaluation-sessions/generate-options`,
      params: { pkRegAssociation, fkRegAssociationDivision },
      type: 'get',
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchRegScoutReportEvaluationOptions = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regScoutReportEvaluationOptions',
      url: `/api/registration/admin/evaluations/${pkRegAssociation}/options`,
      type: 'get',
      successAction: 'success'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchScoutReportEvaluationPlayers = (dispatch) => async (
  pkRegAssociation,
  { pkRegAssociationDivision, pkRegAssociationSeason, regFormUuid } = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  await fetchListResource(
    dispatch,
    'regScoutReportEvaluationPlayers',
    `/api/registration/admin/evaluations/players/${pkRegAssociation}`,
    {
      params: { pkRegAssociationDivision, pkRegAssociationSeason, regFormUuid },
      filter: {
        pkRegAssociation,
        pkRegAssociationDivision,
        pkRegAssociationSeason,
        regFormUuid
      },
      onSuccessCallback,
      onErrorCallback
    }
  );
};

const fetchPaginatedRegScoutReportTemplates = (dispatch) => async (
  pkRegAssociation,
  { search, sort, page, pageSize, appendResults, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedRegScoutReportTemplates',
      url: `/api/registration/admin/scout-reports/templates`,
      type: 'get',
      params: {
        pkRegAssociation,
        search,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchScoutReportTemplates = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regScoutReports'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/admin/scout-reports`,
      {
        params: { pkRegAssociation },
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regScoutReports',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regScoutReports', dispatch, onErrorCallback);
  }
};

const fetchRegScoutReport = (dispatch) => async (
  pkRegScoutReport,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regScoutReport'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/admin/scout-reports/${pkRegScoutReport}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regScoutReport',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regScoutReport', dispatch, onErrorCallback);
  }
};

const createRegScoutReport = (dispatch) => async (
  pkRegAssociation,
  { name, pkRegScoutReportCopy, regScoutReportQuestions },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regScoutReports'
    });
    const response = await sportsheadzApi.post(
      `/api/registration/admin/scout-reports/${pkRegAssociation}`,
      {
        name,
        pkRegScoutReportCopy,
        regScoutReportQuestions
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regScoutReports',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regScoutReports', dispatch, onErrorCallback);
  }
};

const updateRegScoutReport = (dispatch) => async (
  pkRegScoutReport,
  { name, regScoutReportQuestions },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regScoutReport'
    });
    const response = await sportsheadzApi.put(
      `/api/registration/admin/scout-reports/${pkRegScoutReport}`,
      {
        name,
        regScoutReportQuestions
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regScoutReport',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regScoutReport', dispatch, onErrorCallback);
  }
};

const deleteRegScoutReport = (dispatch) => async (
  pkRegScoutReport,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(
      `/api/registration/admin/scout-reports/${pkRegScoutReport}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }

    dispatch({
      type: 'remove',
      stateName: 'regScoutReports',
      payload: (f) => f.pkRegScoutReport !== pkRegScoutReport
    });
  } catch (e) {
    handleError(e, 'regScoutReports', dispatch, onErrorCallback);
  }
};

const fetchPaginatedRegScoutReportEvaluationSessions = (dispatch) => async (
  pkRegAssociation,
  {
    search,
    fkRegAssociationDivision,
    sort,
    page,
    pageSize,
    appendResults,
    skipLoading
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedRegScoutReportEvaluationSessions',
      url: `/api/registration/admin/scout-reports/evaluation-sessions`,
      type: 'get',
      params: {
        pkRegAssociation,
        fkRegAssociationDivision,
        search,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchScoutReportEvaluationSession = (dispatch) => async (
  pkRegScoutReportEvaluationSession,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regScoutReportEvaluationSession'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/admin/scout-reports/evaluation-sessions/${pkRegScoutReportEvaluationSession}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regScoutReportEvaluationSession',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationSession',
      dispatch,
      onErrorCallback
    );
  }
};

const createRegScoutReportEvaluationSession = (dispatch) => async (
  {
    name,
    pkRegScoutReportEvaluationSessionCopy,
    fkRegAssociationDivision,
    regScoutReportEvaluations,
    regScoutReportEvaluationSessionEntries,
    regScoutReportEvaluationSessionTemplates
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/scout-reports/evaluation-sessions`,
      {
        name,
        pkRegScoutReportEvaluationSessionCopy,
        fkRegAssociationDivision,
        regScoutReportEvaluations,
        regScoutReportEvaluationSessionEntries,
        regScoutReportEvaluationSessionTemplates
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regScoutReportEvaluationSessions',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationSessions',
      dispatch,
      onErrorCallback
    );
  }
};

const updateRegScoutReportEvaluationSession = (dispatch) => async (
  pkRegScoutReportEvaluationSession,
  {
    name,
    regScoutReportEvaluations,
    regScoutReportEvaluationSessionEntries,
    regScoutReportEvaluationSessionTemplates
  },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/scout-reports/evaluation-sessions/${pkRegScoutReportEvaluationSession}`,
      {
        name,
        regScoutReportEvaluations,
        regScoutReportEvaluationSessionEntries,
        regScoutReportEvaluationSessionTemplates
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regScoutReportEvaluationSession',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationSessions',
      dispatch,
      onErrorCallback
    );
  }
};

const deleteRegScoutReportEvaluationSession = (dispatch) => async (
  pkRegScoutReportEvaluationSession,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(
      `/api/registration/admin/scout-reports/evaluation-sessions/${pkRegScoutReportEvaluationSession}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    if (onSuccessCallback) {
      onSuccessCallback();
    }

    dispatch({
      type: 'remove',
      stateName: 'regScoutReportEvaluationSessions',
      payload: (f) =>
        f.pkRegScoutReportEvaluationSession !==
        pkRegScoutReportEvaluationSession
    });
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationSessions',
      dispatch,
      onErrorCallback
    );
  }
};

const updateRegScoutReportEvaluationSessionEntry = (dispatch) => async (
  pkRegScoutReportEvaluationSession,
  { pkRegScoutReportEvaluationSessionEntry, regScoutReportEvaluations },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/scout-reports/evaluation-sessions/${pkRegScoutReportEvaluationSession}/entry`,
      {
        pkRegScoutReportEvaluationSessionEntry,
        regScoutReportEvaluations
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regScoutReportEvaluationSession',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationSessions',
      dispatch,
      onErrorCallback
    );
  }
};

const fetchPaginatedRegScoutReportPerson = (dispatch) => async (
  pkRegAssociation,
  {
    search,
    fkRegAssociationDivision,
    playerType,
    sort,
    page,
    pageSize,
    appendResults,
    skipLoading
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'paginatedRegScoutReportPersons',
      url: `/api/registration/admin/scout-reports/persons`,
      type: 'get',
      params: {
        pkRegAssociation,
        fkRegAssociationDivision,
        playerType,
        search,
        sort,
        page,
        pageSize,
        appendResults
      },
      successAction: appendResults ? 'paginated-append' : 'success',
      transformPayload: (data) => ({ ...data }),
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createRegScoutReportPerson = (dispatch) => async (
  { firstName, lastName, email, phone, gender, role, fkRegAssociationDivision },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/scout-reports/persons`,
      {
        firstName,
        lastName,
        email,
        phone,
        gender,
        role,
        fkRegAssociationDivision
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'append',
      stateName: 'regScoutReportEvaluationPlayers',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationPlayers',
      dispatch,
      onErrorCallback
    );
  }
};

const createRegScoutReportPersons = (dispatch) => async (
  regScoutReportPersons,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/scout-reports/persons/bulk`,
      regScoutReportPersons,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regScoutReportEvaluationPlayers',
      payload: { value: response.data }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationPlayers',
      dispatch,
      onErrorCallback
    );
  }
};

const updateRegScoutReportPerson = (dispatch) => async (
  pkRegScoutReportPerson,
  { firstName, lastName, email, phone, gender, role, fkRegAssociationDivision },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/scout-reports/persons/${pkRegScoutReportPerson}`,
      {
        firstName,
        lastName,
        email,
        phone,
        gender,
        role,
        fkRegAssociationDivision
      },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regScoutReportEvaluationPlayers',
      payload: {
        value: response.data,
        filter: (f) => f.pkRegScoutReportPerson !== response.data.entityId
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationPlayers',
      dispatch,
      onErrorCallback
    );
  }
};

const deleteRegScoutReportPerson = (dispatch) => async (
  pkRegScoutReportPerson,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    await sportsheadzApi.delete(
      `/api/registration/admin/scout-reports/persons/${pkRegScoutReportPerson}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'remove',
      stateName: 'regScoutReportEvaluationPlayers',
      payload: (f) => f.pkRegScoutReportPerson !== pkRegScoutReportPerson
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(
      e,
      'regScoutReportEvaluationPlayers',
      dispatch,
      onErrorCallback
    );
  }
};
// #endregion

// #region Merchandise
const fetchMerchPackageOrders = (dispatch) => async (
  {
    fkRegAssociation,
    merchPackageUuid,
    purchasedAfter,
    purchasedBefore,
    analyticsCta,
    search,
    status,
    page,
    pageSize,
    sortBy,
    orderBy
  },
  appendResults = false
) => {
  try {
    if (!appendResults) {
      dispatch({
        type: 'fetching',
        stateName: 'paginatedRegCartMerchPackages',
        payload: {
          value: [],
          filter: {
            fkRegAssociation,
            merchPackageUuid,
            purchasedAfter,
            purchasedBefore,
            analyticsCta,
            search,
            status,
            page,
            pageSize,
            sortBy,
            orderBy
          }
        }
      });
    }

    const response = await sportsheadzApi.get(
      `/api/registration/merchandise/orders`,
      {
        params: {
          fkRegAssociation,
          merchPackageUuid,
          purchasedAfter,
          purchasedBefore,
          analyticsCta,
          search,
          status,
          page,
          pageSize,
          sortBy,
          orderBy
        },
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );

    dispatch({
      type: appendResults ? 'paginated-append' : 'success',
      stateName: 'paginatedRegCartMerchPackages',
      payload: {
        pageIndex: response.data.pageIndex,
        pageSize: response.data.pageSize,
        totalCount: response.data.totalCount,
        totalPages: response.data.totalPages,
        data: response.data.data
      }
    });
  } catch {
    triggerNotification('Oops something went wrong');
    dispatch({
      type: 'error',
      stateName: 'paginatedRegCartMerchPackages',
      payload: 'Oops something went wrong'
    });
  }
};

const fetchMerchPackageOrderFilters = (dispatch) => async (
  fkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regCartMerchPackageFilters',
      payload: {
        value: null,
        filter: { fkRegAssociation }
      }
    });

    const response = await sportsheadzApi.get(
      '/api/registration/merchandise/orders/filters',
      {
        params: { fkRegAssociation },
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );

    dispatch({
      type: 'success',
      stateName: 'regCartMerchPackageFilters',
      payload: {
        filter: { fkRegAssociation },
        value: response.data
      }
    });

    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regCartMerchPackageFilters', dispatch, onErrorCallback);
  }
};

const updateMerchPackageDetails = (dispatch) => async (
  pkRegCartMerchPackage,
  { inputAnswers },
  onSuccess,
  onError
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/staff/merchandise/orders/${pkRegCartMerchPackage}`,
      { inputAnswers },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );

    dispatch({
      type: 'paginated-replace-index',
      stateName: 'paginatedRegCartMerchPackages',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegCartMerchPackage === response.data.pkRegCartMerchPackage
      }
    });
    if (onSuccess) {
      onSuccess();
    }
  } catch (e) {
    handleError(e, 'paginatedRegCartMerchPackages', dispatch, onError);
  }
};

const fetchRegCartMerchPackageBalance = (dispatch) => async (
  pkRegCartMerchPackage,
  onSuccess,
  onError
) => {
  try {
    dispatch({
      type: 'fetching',
      stateName: 'regCartMerchPackageBalance'
    });
    const response = await sportsheadzApi.get(
      `/api/registration/merchandise/orders/${pkRegCartMerchPackage}/balance`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regCartMerchPackageBalance',
      payload: { value: response.data }
    });
    if (onSuccess) {
      onSuccess();
    }
  } catch (e) {
    handleError(e, 'regCartMerchPackageBalance', dispatch, onError);
  }
};

const refundRegCartMerchPackage = (dispatch) => async (
  pkRegCartMerchPackage,
  { partialRefundAmount },
  onSuccess,
  onError
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/payment/merchandise/refund/${pkRegCartMerchPackage}`,
      { partialRefundAmount },
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      }
    );

    dispatch({
      type: 'paginated-replace-index',
      stateName: 'paginatedRegCartMerchPackages',
      payload: {
        value: response.data,
        filter: (s) =>
          s.pkRegCartMerchPackage === response.data.pkRegCartMerchPackage
      }
    });
    if (onSuccess) {
      onSuccess();
    }
  } catch (e) {
    handleError(e, 'regCartMerchPackageBalance', dispatch, onError);
  }
};
// #endregion

// #region EcomStore Orders
const fetchPaginatedStoreOrders = (dispatch) => async (
  pkRegAssociation,
  {
    search,
    status,
    isBulkShipment,
    escrowOrders,
    fkUserAdmin,
    vendorProductUuids,
    sort,
    page,
    pageSize,
    appendResults,
    pkEcomOrders,
    skipStateUpdate
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const filter = {
    pkRegAssociation,
    search,
    status,
    isBulkShipment,
    escrowOrders,
    fkUserAdmin,
    sort,
    page,
    pageSize,
    vendorProductUuids,
    pkEcomOrders
  };
  const searchParams = buildURLSearchParams(filter);

  onApiRequest(
    {
      url: `/api/registration/admin/association/${pkRegAssociation}/shop-orders?${searchParams.toString()}`,
      type: 'get',
      filter,
      transformPayload: (data) => ({ ...data }),
      ...(skipStateUpdate
        ? {}
        : {
            successAction: appendResults ? 'paginated-append' : 'success',
            dispatch,
            stateName: 'paginatedEcomOrders'
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Administrators & Permissions
const fetchRegAssociationAdministrators = (dispatch) => async (
  pkRegAssociation,
  { includeDeleted },
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(
    dispatch,
    'regAssociationAdmins',
    `/api/registration/admin/administrators/${pkRegAssociation}`,
    {
      params: { includeDeleted },
      filter: { pkRegAssociation },
      onSuccessCallback,
      onErrorCallback
    }
  );
};

const createRegAssociationAdministrator = (dispatch) => async (
  regAssociationAdmins,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/administrators`,
      regAssociationAdmins,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationAdmins',
      payload: {
        filter: (f) =>
          !response.data.some(
            (d) => d.pkRegAssociationAdmin === f.pkRegAssociationAdmin
          ),
        value: response.data
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regAssociationAdmins', dispatch, onErrorCallback);
  }
};

const updateRegAssociationAdministrator = (dispatch) => async (
  regAssociationAdmins,
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.put(
      `/api/registration/admin/administrators`,
      regAssociationAdmins,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationAdmins',
      payload: {
        filter: (f) =>
          !response.data.some(
            (d) => d.pkRegAssociationAdmin === f.pkRegAssociationAdmin
          ),
        value: response.data
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback(response.data);
    }
  } catch (e) {
    handleError(e, 'regAssociationAdmins', dispatch, onErrorCallback);
  }
};

const fetchRegAssociationAdminInvite = (dispatch) => async (inviteCode) => {
  dispatch({
    type: 'fetching',
    stateName: 'regAssociationAdminInvite'
  });
  try {
    const response = await sportsheadzApi.get(
      `/api/registration/admin/administrators/invite/${inviteCode}`,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'success',
      stateName: 'regAssociationAdminInvite',
      payload: { value: response.data }
    });
  } catch (e) {
    handleError(e, 'regAssociationAdminInvite', dispatch);
  }
};

const respondToRegAssociationAdminInvite = (dispatch) => async (
  pkRegAssociationAdmin,
  { isAccepted },
  onSuccessCallback,
  onErrorCallback
) => {
  try {
    const response = await sportsheadzApi.post(
      `/api/registration/admin/administrators/invite/${pkRegAssociationAdmin}/respond?isAccepted=${isAccepted}`,
      null,
      {
        headers: {
          Authorization: `Bearer ${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        withCredentials: true
      }
    );
    dispatch({
      type: 'replace',
      stateName: 'regAssociationAdmins',
      payload: {
        filter: (f) => pkRegAssociationAdmin !== f.pkRegAssociationAdmin,
        value: response.data
      }
    });
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  } catch (e) {
    handleError(e, 'regAssociationAdmins', dispatch, onErrorCallback);
  }
};

const fetchAdminRegAssociationEmailUnsubscribe = (dispatch) => async (
  pkRegAssociation,
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(
    dispatch,
    'regEmailUnsubscribe',
    `/api/registration/mailings/unsubscribe/${pkRegAssociation}`,
    {
      filter: { pkRegAssociation },
      onSuccessCallback,
      onErrorCallback
    }
  );
};

const fetchAdminRegAssociationSentEmails = (dispatch) => async (
  onSuccessCallback,
  onErrorCallback
) => {
  await fetchListResource(
    dispatch,
    'sesEmailSessions',
    `/api/registration/mailings/sent/`,
    {
      onSuccessCallback,
      onErrorCallback
    }
  );
};
// #endregion

// #region Task templates
const fetchRegAssociationTaskTemplates = (dispatch) => async (
  pkRegAssociation,
  {} = {},
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/registration/admin/task-templates`,
      type: 'get',
      params: { pkRegAssociation },
      successAction: 'success',
      dispatch,
      stateName: 'regAssociationTaskTemplates'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const createRegAssociationTaskTemplates = (dispatch) => async (
  pkRegAssociation,
  { regAssociationTaskTemplates },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationTaskTemplates',
      url: `/api/registration/admin/task-templates/${pkRegAssociation}`,
      type: 'post',
      data: regAssociationTaskTemplates,
      successAction: 'append'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateRegAssociationTaskTemplates = (dispatch) => async (
  pkRegAssociation,
  { regAssociationTaskTemplates },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationTaskTemplates',
      url: `/api/registration/admin/task-templates/${pkRegAssociation}`,
      type: 'put',
      data: regAssociationTaskTemplates,
      successAction: 'replace',
      removeFilter: (item) =>
        !regAssociationTaskTemplates.some(
          (t) =>
            t.pkRegAssociationTaskTemplate.toString() ===
            item.pkRegAssociationTaskTemplate.toString()
        )
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteRegAssociationTaskTemplates = (dispatch) => async (
  pkRegAssociation,
  pkRegAssociationTaskTemplates,
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkRegAssociationTaskTemplateParam = pkRegAssociationTaskTemplates?.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkRegAssociationTaskTemplate[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociationTaskTemplates',
      url: `/api/registration/admin/task-templates/${pkRegAssociation}?${pkRegAssociationTaskTemplateParam}`,
      type: 'delete',
      removeFilter: (item) =>
        !pkRegAssociationTaskTemplates.includes(
          item.pkRegAssociationTaskTemplate
        )
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region Players
const fetchPaginatedRegPersons = (dispatch) => async (
  pkRegAssociation,
  {
    search,
    status,
    sort,
    page,
    pageSize,
    isBenchStaff,
    appendResults,
    skipStateUpdate
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/registration/admin/association/${pkRegAssociation}/players`,
      type: 'get',
      params: {
        pkRegAssociation,
        search,
        status,
        isBenchStaff,
        sort,
        page,
        pageSize
      },
      transformPayload: (data) => ({ ...data }),
      ...(skipStateUpdate
        ? {}
        : {
            successAction: appendResults ? 'paginated-append' : 'success',
            dispatch,
            stateName: 'paginatedRegPersons'
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchRegAssociationRegPerson = (dispatch) => async (
  pkRegAssociation,
  { pkRegPerson },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regPerson',
      url: `/api/registration/admin/association/${pkRegAssociation}/players/${pkRegPerson}`,
      type: 'get',
      filter: { pkRegAssociation, pkRegPerson },
      successAction: 'success',
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchPaginatedRegCustomers = (dispatch) => async (
  pkRegAssociation,
  { search, status, sort, page, pageSize, appendResults, skipStateUpdate },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/registration/admin/association/${pkRegAssociation}/customers`,
      type: 'get',
      params: {
        pkRegAssociation,
        search,
        status,
        sort,
        page,
        pageSize
      },
      transformPayload: (data) => ({ ...data }),
      ...(skipStateUpdate
        ? {}
        : {
            successAction: appendResults ? 'paginated-append' : 'success',
            dispatch,
            stateName: 'paginatedRegCustomers'
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchRegAssociationCustomer = (dispatch) => async (
  pkRegAssociation,
  { pkUser },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regCustomer',
      url: `/api/registration/admin/association/${pkRegAssociation}/customers/${pkUser}`,
      type: 'get',
      filter: { pkRegAssociation, pkUser },
      successAction: 'success',
      resetStateValue: null
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region RegResource
const updateRegResource = (dispatch) => async (
  pkRegAssociation,
  {
    resourceId,
    fkRegResourceType,
    regResourceLogs,
    regResourceTaskTemplates,
    regResourceTaskTemplateAnswers
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regResource',
      successAction: 'success',
      url: `/api/registration/admin/association/${pkRegAssociation}/resources`,
      type: 'put',
      data: {
        resourceId,
        fkRegResourceType,
        regResourceLogs,
        regResourceTaskTemplates,
        regResourceTaskTemplateAnswers
      }
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateRegAssociationExternalConnections = (dispatch) => async (
  pkRegAssociation,
  { regAssociationExternalConnections },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      dispatch,
      stateName: 'regAssociations',
      successAction: 'replace',
      url: `/api/registration/admin/association/${pkRegAssociation}/external-connections`,
      type: 'put',
      data: regAssociationExternalConnections,
      removeFilter: (a) =>
        a.pkRegAssociation.toString() !== pkRegAssociation.toString()
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const deleteRegAssociationExternalConnections = (dispatch) => async (
  pkRegAssociation,
  { pkRegAssociationExternalConnections },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const pkRegAssociationExternalConnectionParam = pkRegAssociationExternalConnections.reduce(
    // eslint-disable-next-line no-return-assign
    (result, current, index) =>
      // eslint-disable-next-line no-param-reassign
      (result += `pkRegAssociationExternalConnection[${index}]=${current}&`),
    ''
  );

  onApiRequest(
    {
      url: `/api/registration/admin/association/${pkRegAssociation}/external-connections?${pkRegAssociationExternalConnectionParam}`,
      type: 'delete'
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region RegCart Orders
const fetchPaginatedRegCartOrders = (dispatch) => async (
  pkRegAssociation,
  {
    search,
    status,
    sort,
    page,
    pageSize,
    pkRegCarts,
    pkEcomOrders,
    appendResults,
    skipStateUpdate
  },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const searchParams = buildURLSearchParams({
    search,
    status,
    sort,
    page,
    pageSize,
    pkRegCarts,
    pkEcomOrders,
    skipStateUpdate
  });

  onApiRequest(
    {
      url: `/api/registration/admin/association/${pkRegAssociation}/orders?${searchParams.toString()}`,
      type: 'get',
      filter: {
        pkRegAssociation,
        search,
        status,
        sort,
        page,
        pageSize,
        pkEcomOrders
      },
      transformPayload: (data) => ({ ...data }),
      ...(skipStateUpdate
        ? {}
        : {
            successAction: appendResults ? 'paginated-append' : 'success',
            dispatch,
            stateName: 'paginatedRegCartOrders'
          })
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const fetchRegCartOrder = (dispatch) => async (
  { pkRegCart, pkRegFormSubmission, pkEcomOrder, skipLoading },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  const filter = { pkRegCart, pkRegFormSubmission, pkEcomOrder };
  const searchParams = buildURLSearchParams(filter);

  onApiRequest(
    {
      dispatch,
      stateName: 'regCart',
      url: `/api/registration/admin/association/orders?${searchParams.toString()}`,
      type: 'get',
      filter,
      successAction: 'success',
      resetStateValue: null,
      skipLoading
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

// #region RegCartPayments
const processRegCartPaymentInstallmentImmediately = (dispatch) => async (
  pkRegCart,
  { pkRegCartPaymentInstallment },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/payment/registration/orders/${pkRegCart}/installments/${pkRegCartPaymentInstallment}/process`,
      type: 'post',
      successAction: 'success',
      dispatch,
      stateName: 'regCart'
    },
    onSuccessCallback,
    onErrorCallback
  );
};

const updateRegCartPaymentInstallment = (dispatch) => async (
  pkRegCart,
  { pkRegCartPaymentInstallment, totalInCents, chargeDate },
  onSuccessCallback = null,
  onErrorCallback = null
) => {
  onApiRequest(
    {
      url: `/api/payment/registration/orders/${pkRegCart}/installments/${pkRegCartPaymentInstallment}`,
      type: 'put',
      data: {
        totalInCents,
        chargeDate
      },
      successAction: 'success',
      dispatch,
      stateName: 'regCart'
    },
    onSuccessCallback,
    onErrorCallback
  );
};
// #endregion

export const { Provider, Context } = createDataProvider(
  providerContextReducer,
  {
    changeRegistrationDivision,
    changeRegistrationOverrideEmail,
    createAdminRegAssociationCoupon,
    createAdminRegAssociationDivision,
    createAdminRegAssociationDivisionTeam,
    createAdminRegAssociationGroupCoupon,
    createAdminRegAssociationPaymentPlan,
    createAdminRegAssociationSavedTemplate,
    createAdminRegAssociationSeason,
    createAdminRegForm,
    createAdminRegFormSubmissionPayment,
    createRegAssociationAdministrator,
    createRegAssociationTaskTemplates,
    createRegScoutReportEvaluationSession,
    createRegScoutReportPerson,
    createRegScoutReportPersons,
    createRegScoutReport,
    creditAdminRegistrationInvoice,
    deleteAdminRegAssociationDivision,
    deleteAdminRegAssociationDivisionTeam,
    deleteAdminRegAssociationSavedTemplate,
    deleteAdminRegAssociationSeason,
    deleteRegAssociationExternalConnections,
    deleteRegAssociationTaskTemplates,
    deleteRegScoutReportEvaluationSession,
    deleteRegScoutReportPerson,
    deleteRegScoutReport,
    deleteAdminRegAssociationPaymentPlans,
    deleteAdminRegForm,
    disconnectPaymentProvider,
    expireAdminRegAssociationCoupon,
    expireAdminRegAssociationDivisionFormExternalInvite,
    expireAdminRegAssociationDivisionFormInvite,
    expireAdminRegAssociationGroupCoupon,
    generateAdminRegAssociationDivisionTeams,
    generateAdminRegAssociationReport,
    generateAdminRegAssociationSavedTemplate,
    generateStaffRegistrationReport,
    importRegAssociationDivisionTeams,
    onboardPaymentProvider,
    updateRegAssociation,
    updateRegFormSubmission,
    updateRegFormSubmissionsBulk,
    updateRegResource,
    updateAdminRegAssociationCoupon,
    updateAdminRegAssociationDivision,
    updateAdminRegAssociationDivisionTeam,
    updateAdminRegAssociationGroupCoupon,
    updateAdminRegAssociationPaymentPlans,
    updateAdminRegAssociationSavedTemplate,
    updateAdminRegAssociationSeason,
    updateAdminRegistrationInvoiceStatus,
    updateAdminRegSpecialRequests,
    updateAdminRegForm,
    updateMerchPackageDetails,
    updateRegAssociationAdministrator,
    updateRegAssociationExternalConnections,
    updateRegAssociationTaskTemplates,
    updateRegCartPaymentInstallment,
    updateRegScoutReportEvaluationSession,
    updateRegScoutReportEvaluationSessionEntry,
    updateRegScoutReportPerson,
    updateRegScoutReport,
    fetchAdminFinancialRegistrations,
    generateAdminRegAssociationAnalyticsReport,
    fetchAdminRegAssociationCoupons,
    fetchAdminRegAssociationDivisionFormInvites,
    fetchAdminRegAssociationDivisions,
    fetchAdminRegAssociationDivisionRegistrants,
    fetchAdminRegAssociationEmailUnsubscribe,
    fetchAdminRegAssociationLeagueOptions,
    fetchAdminRegAssociationPaymentPlan,
    fetchAdminRegAssociationPaymentPlans,
    fetchAdminRegAssociationRegistrants,
    fetchAdminRegAssociations,
    fetchAdminRegAssociationSeasons,
    fetchAdminRegAssociationSentEmails,
    fetchAdminRegFormSubmissionOrderHistory,
    fetchAdminRegFormSubmissionPayments,
    fetchAdminRegFormSubmissionPaymentTransactionDetails,
    fetchAdminRegFormSubmissionInvoiceTransactionDetails,
    fetchAdminRegForm,
    fetchAdminRegForms,
    fetchAdminRegFormVersionHistory,
    fetchAdminRegReports,
    fetchAdminRegReportInputSources,
    fetchAdminRegSpecialRequests,
    fetchAdminWaitlistRegistrants,
    fetchMerchPackageOrderFilters,
    fetchMerchPackageOrders,
    fetchPaginatedCustomReports,
    fetchPaginatedRegCustomers,
    fetchPaginatedRegFormSubmissions,
    fetchPaginatedRegPersons,
    fetchPaginatedStoreOrders,
    fetchRegAssociationAdministrators,
    fetchRegAssociationAdminInvite,
    fetchRegAssociationCustomer,
    fetchRegAssociationDivisionTeamOptions,
    fetchRegAssociationDivisionTeams,
    fetchRegAssociationOptionsSummary,
    fetchRegAssociationRegPerson,
    fetchRegAssociationResourceOptions,
    fetchRegAssociationTaskTemplates,
    fetchRegCartOrder,
    fetchRegCartMerchPackageBalance,
    fetchRegFormSubmissionRegistration,
    fetchRegFormSubmissionExportData,
    fetchRegScoutReport,
    fetchRegScoutReportEvaluationGenerateTeamOptions,
    fetchRegScoutReportEvaluationOptions,
    fetchUpdatedAdminFinancialRegistration,
    fetchUpdatedAdminRegAssociationDivision,
    fetchPaginatedRegCartOrders,
    fetchPaginatedRegScoutReportEvaluationSessions,
    fetchPaginatedRegScoutReportPerson,
    fetchPaginatedRegScoutReportTemplates,
    fetchScoutReportEvaluationPlayers,
    fetchScoutReportEvaluationSession,
    fetchScoutReportTemplates,
    processRegCartPaymentInstallmentImmediately,
    rebaseAdminRegistrationInvoice,
    rebaseRegAssociationDivisionTeamConnection,
    respondToFinancialRegistration,
    refundFinancialRegistration,
    refundRegCartMerchPackage,
    removeAdminWaitlistPerson,
    resendRegCartOrderReceipts,
    sendAdminRegAssociationDivisionFormInvites,
    sendAdminWaitlistEmail,
    syncRegAssociationSeasonTeams,
    resendAdminRegistrationInvoice,
    respondToRegAssociationAdminInvite,
    sendAdminRegistrationEmail,
    sendAdminRegistrationInvoice,
    sendAdminWaitlistReservation
  },
  initialState
);
